// ─────────────────────────────────────────────────────────
// services/auth.ts — Supabase Auth + role resolution
//
// Phase 3 changes:
//   + loginSuccess() no longer calls fbLoad() directly
//   + Instead it sets the user in authStore and allows
//     useCompanyData hook (in AppLayout) to trigger the
//     TanStack Query data load automatically
//   + USE_SUPABASE_DB flag respected in getUserDoc / getCompanyDoc
//     (both live in repository.ts which already has the gates)
//
// All exported function signatures are UNCHANGED.
// ─────────────────────────────────────────────────────────
import { supabase }                           from '@/supabase'
import { initEncryption, clearEncryptionKey } from './encryption'
import { getUserDoc, getCompanyDoc }           from './repository'
import { idbClearAll }                        from './idb'
import { idbLoad }                             from './idbSync'
import { idbLoadBrand }                       from './idbSync'
import { queryClient }                         from '@/lib/queryClient'
import { QUERY_KEYS }                          from '@/lib/queryKeys'
import { FLAGS }                               from '@/lib/flags'
import { useAuthStore }                        from '@/store/authStore'
import { useDataStore }                        from '@/store/dataStore'
import { useUIStore }                          from '@/store/uiStore'
import { resetSyncEngine }                    from './syncEngine'
import { sentrySetUser, sentryClearUser }      from '@/lib/sentry'
import type { AppUser, Role, Brand }           from '@/types'

// ── PERMISSIONS ───────────────────────────────────────────
export const PERMISSIONS: Record<string, {
  modules:          'all' | string[]
  write:            'all' | 'none'
  canManageUsers:   boolean
  canManageBilling: boolean
  canSeeTenants:    boolean
  canImpersonate:   boolean
}> = {
  super_admin:  { modules:'all', write:'all',  canManageUsers:true,  canManageBilling:true,  canSeeTenants:true,  canImpersonate:true  },
  master_admin: { modules:'all', write:'all',  canManageUsers:true,  canManageBilling:true,  canSeeTenants:false, canImpersonate:false },
  admin:        { modules:'all', write:'all',  canManageUsers:false, canManageBilling:false, canSeeTenants:false, canImpersonate:false },
  auditor:      { modules:'all', write:'none', canManageUsers:false, canManageBilling:false, canSeeTenants:false, canImpersonate:false },
  viewer:       { modules:[],   write:'none', canManageUsers:false, canManageBilling:false, canSeeTenants:false, canImpersonate:false },
}

// ── Map Supabase errors → .code ───────────────────────────
function mapAuthError(msg: string): Error {
  const e = new Error(msg) as Error & { code?: string }
  if (/invalid.*credential|invalid.*password|user.*not.*found|invalid.*email/i.test(msg))
    e.code = 'auth/wrong-password'
  else if (/too.*many.*request/i.test(msg))
    e.code = 'auth/too-many-requests'
  else if (/network|fetch/i.test(msg))
    e.code = 'auth/network-request-failed'
  return e
}

// ── CSS brand colour ──────────────────────────────────────
function applyBrandColor(color?: string): void {
  if (!color) { document.documentElement.style.removeProperty('--color-ac'); return }
  if (/^#[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(color))
    document.documentElement.style.setProperty('--color-ac', color)
  else
    document.documentElement.style.removeProperty('--color-ac')
}

// ── Login ─────────────────────────────────────────────────
export async function doLogin(email: string, password: string): Promise<void> {
  if (!email || !password) throw new Error('Please enter email and password')

  const { data, error } = await supabase.auth.signInWithPassword({
    email: email.trim().toLowerCase(), password,
  })
  if (error) throw mapAuthError(error.message)

  const u = data.user
  const displayName = (u.user_metadata?.display_name as string | undefined)
    ?? email.split('@')[0]!.replace(/[._]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

  await loginSuccess(u.id, displayName, email.trim().toLowerCase())
}

// ── Resolve role, set user, trigger data query ────────────
// Phase 3: no longer calls fbLoad directly.
// The data load is triggered by useCompanyData hook
// (mounted in AppLayout) reacting to user being set in authStore.

// Guard: doLogin calls loginSuccess directly AND the SIGNED_IN listener calls
// it again concurrently (setUser happens mid-function, so the current?.uid
// check in the listener doesn't protect against the race). This module-level
// flag ensures only the first caller runs; subsequent calls for the same UID
// return immediately and let the first caller finish.
let _loginInProgressForUid: string | null = null

export async function loginSuccess(uid: string, displayName: string, email: string): Promise<void> {
  if (_loginInProgressForUid === uid) return
  _loginInProgressForUid = uid

  const { setUser, setBrand, setLoading } = useAuthStore.getState()
  const { showToast } = useUIStore.getState()

  setLoading(true)
  try {

  let user: AppUser = { uid, email, displayName, role: 'master_admin', companyId: '' }

  // 1. Resolve role from public.users
  //    Skipped entirely when USE_SUPABASE_DB=false — no DB to query
  try {
    const profile = await getUserDoc(uid) as Record<string, unknown> | null
    if (profile) {
      user = {
        ...user,
        displayName: (profile.name as string) ?? displayName,
        role:        (profile.role as Role)   ?? 'master_admin',
        companyId:   (profile.companyId as string) ?? '',
      }
    } else if (FLAGS.USE_SUPABASE_DB) {
      // Check for invite metadata — set when admin invited this user via Edge Function
      const { data: authData } = await supabase.auth.getUser()
      const meta = authData?.user?.user_metadata ?? {}
      const inviteRole      = (meta.role as string | undefined) ?? 'admin'
      const inviteCompanyId = (meta.company_id as string | undefined) ?? uid
      const inviteName      = (meta.display_name as string | undefined) ?? displayName

      const healDoc = {
        id: uid, company_id: inviteCompanyId, role: inviteRole,
        name: inviteName, email,
        invite_pending: false,
        created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
      }
      supabase.from('users').upsert(healDoc, { onConflict: 'id' }).then(({ error: e }) => {
        if (e) console.warn('Self-heal user upsert error:', e.message)
      })
      // If this is a fresh invite (no company yet), also create the company
      if (!meta.company_id) {
        supabase.from('companies').insert({
          id: uid, name: inviteName + "'s Company", status: 'active', plan: 'Free',
          created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
        }).then(({ error: e }) => {
          if (e && !e.message.includes('duplicate')) console.warn('Self-heal company insert:', e.message)
        })
      }
      user.companyId = inviteCompanyId
      user.role      = inviteRole as Role
      user.displayName = inviteName
    } else {
      // DB disabled — use uid as companyId so the user gets a local workspace
      user.companyId = uid
    }
  } catch { /* continue with defaults */ }

  // 2. Validate company status
  if (user.companyId && user.role !== 'super_admin') {
    try {
      const co = await getCompanyDoc(user.companyId) as Record<string, unknown> | null
      if ((co?.status as string) === 'suspended') {
        await supabase.auth.signOut()
        throw new Error('Your account has been suspended. Contact support.')
      }
    } catch (e) {
      if (e instanceof Error && e.message.includes('suspended')) throw e
    }
  }

  // 3. Fallback companyId
  if (!user.companyId && user.role !== 'super_admin') {
    user.companyId = uid
  }

  // 4. Init encryption
  if (user.companyId) await initEncryption(user.companyId)

  // 5. Set user in store — triggers useCompanyData hook
  setUser(user)
  sentrySetUser(user.uid, user.email)

  // 5b. Phase 4: Seed Zustand from IDB immediately (instant, offline-safe)
  //     This runs before the TanStack Query prefetch so the UI has data
  //     to render even if the network is slow or unavailable.
  if (user.companyId) {
    const hadIDBData = await idbLoad(user.companyId)

    // If IDB had data, also restore brand from IDB (logo, colour)
    if (hadIDBData) {
      try {
        const cachedBrand = await idbLoadBrand()
        if (cachedBrand?.name) {
          setBrand(cachedBrand as Brand)
          applyBrandColor(cachedBrand.primaryColor)
        }
      } catch { /* non-fatal */ }
    }
  }

  // 6. Prefetch company data into TanStack Query cache
  //    useCompanyData (in AppLayout) will subscribe and hydrate Zustand
  if (user.companyId) {
    try {
      await queryClient.prefetchQuery({
        queryKey: QUERY_KEYS.companyData(user.companyId),
        queryFn:  () => import('./db').then(m => m.fbLoad(user.companyId)),
        staleTime: 5 * 60 * 1000,
      })
      // Brand is applied by useCompanyData hook via useEffect
      const cached = queryClient.getQueryData<Awaited<ReturnType<typeof import('./db').fbLoad>>>(
        QUERY_KEYS.companyData(user.companyId)
      )
      if (cached?.brand?.name) {
        setBrand(cached.brand as Brand)
        applyBrandColor(cached.brand.primaryColor)
      }
    } catch (e) {
      showToast('Data load error', 'error')
      console.error('prefetchQuery error:', e)
    }
  }

    setLoading(false)
  } finally {
    _loginInProgressForUid = null
  }
}

// ── Logout ────────────────────────────────────────────────
export async function doLogout(): Promise<void> {
  const { setUser }    = useAuthStore.getState()
  const { clearAll }   = useDataStore.getState()

  // Clear all TanStack Query caches
  queryClient.clear()

  // Clear IDB data store (Phase 4)
  idbClearAll().catch(() => {})

  // Reset sync engine state + last-sync timestamp (Phase 6)
  resetSyncEngine()

  setUser(null); clearAll(); clearEncryptionKey()

  ;['cu','companyId','brand','proj','tender','emd','recv','exp','cf','vendor',
    'invoice','emp','att','sal','adv','ins','users','receipts','task','boq',
    'daily','audit','retRelease','tdsChallans','bankTxns','einvoices',
    'pettyCash','po','milestones','quotations',
  ].forEach(k => { try { sessionStorage.removeItem(`re_${k}`) } catch { /* ignore */ } })

  sentryClearUser()
  try { await supabase.auth.signOut() } catch { /* reload anyway */ }
  window.location.reload()
}

// ── Auth state listener ───────────────────────────────────
let _listenerAttached = false

export function attachAuthListener(): void {
  if (_listenerAttached) return
  _listenerAttached = true

  const { setUser, setLoading } = useAuthStore.getState()
  setLoading(true)

  // Safety net: if INITIAL_SESSION never fires (Supabase unreachable,
  // network down, service worker serving stale app), clear loading after
  // 6 s so the user sees the login page instead of a stuck spinner.
  const initTimeout = setTimeout(() => {
    if (useAuthStore.getState().isLoading) {
      console.warn('[auth] INITIAL_SESSION timeout — forcing load complete')
      setLoading(false)
    }
  }, 6_000)

  supabase.auth.onAuthStateChange(async (event, session) => {
    clearTimeout(initTimeout)   // Supabase responded — cancel the fallback
    // ── Session expiry / token refresh failure ────────────
    // When the refresh token is expired (user inactive > 7 days on Free plan),
    // Supabase fires TOKEN_REFRESHED with a null session.
    // We treat that as a forced logout with a user-friendly message.
    if (event === 'TOKEN_REFRESHED' && !session) {
      const { showToast } = useUIStore.getState()
      showToast('Your session expired — please sign in again.', 'warning')
      setUser(null); setLoading(false)
      return
    }

    // ── Token refreshed successfully ──────────────────────
    // No user state change needed — the Supabase client already has the
    // new token. Just clear any loading state.
    if (event === 'TOKEN_REFRESHED' && session) {
      setLoading(false)
      return
    }

    // ── User profile updated (email change, metadata) ─────
    // Re-run loginSuccess to pick up the latest display name / email.
    if (event === 'USER_UPDATED' && session?.user) {
      const u = session.user
      const displayName = (u.user_metadata?.display_name as string | undefined)
        ?? u.email?.split('@')[0]?.replace(/[._]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        ?? 'User'
      try {
        await loginSuccess(u.id, displayName, u.email ?? '')
      } catch { /* non-fatal */ }
      return
    }

    // ── Password recovery flow ────────────────────────────
    if (event === 'PASSWORD_RECOVERY') {
      // App should route to /reset-password; handled at router level.
      return
    }

    // ── Standard events ───────────────────────────────────
    if (event !== 'INITIAL_SESSION' && event !== 'SIGNED_IN' && event !== 'SIGNED_OUT') return

    if (event === 'SIGNED_OUT' || !session?.user) {
      setUser(null); setLoading(false); return
    }

    const current = useAuthStore.getState().user
    if (current?.uid === session.user.id) { setLoading(false); return }

    const u = session.user
    const displayName = (u.user_metadata?.display_name as string | undefined)
      ?? u.email?.split('@')[0]?.replace(/[._]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      ?? 'User'

    // ── INITIAL_SESSION (page reload with stored session) ─────
    // Do NOT make network calls here. The service worker's NetworkFirst
    // strategy adds up to 10s per Supabase call; 3 calls × 10s exceeds the
    // safety timeout and the user ends up back on the login page.
    // Role and companyId are already in user_metadata (written at signup/invite).
    // useCompanyData (mounted in AppLayout) fetches fresh data in the background.
    if (event === 'INITIAL_SESSION') {
      const fastUser: AppUser = {
        uid:         u.id,
        email:       u.email ?? '',
        displayName,
        role:        ((u.user_metadata?.role)       as Role   | undefined) ?? 'master_admin',
        companyId:   ((u.user_metadata?.company_id) as string | undefined) ?? u.id,
      }
      if (fastUser.companyId) await initEncryption(fastUser.companyId)
      if (fastUser.companyId) {
        const hadData = await idbLoad(fastUser.companyId).catch(() => false as boolean)
        if (hadData) {
          try {
            const brand = await idbLoadBrand()
            if (brand?.name) {
              useAuthStore.getState().setBrand(brand as Brand)
              applyBrandColor(brand.primaryColor)
            }
          } catch { /* non-fatal */ }
        }
      }
      setUser(fastUser)
      sentrySetUser(fastUser.uid, fastUser.email)
      setLoading(false)
      return
    }

    // ── SIGNED_IN (explicit login click) ──────────────────────
    // Full loginSuccess: resolves role from DB, validates company, seeds IDB,
    // prefetches TanStack Query cache. A few seconds delay is acceptable here.
    const loginTimeoutId = setTimeout(() => {
      if (useAuthStore.getState().isLoading) {
        console.warn('[auth] loginSuccess timeout — forcing load complete')
        useUIStore.getState().showToast('Slow connection — reload if data looks incomplete', 'warning')
        setLoading(false)
      }
    }, 25_000)

    try {
      await loginSuccess(u.id, displayName, u.email ?? '')
    } catch (e) {
      console.warn('loginSuccess error in auth listener:', e)
      setLoading(false)
    } finally {
      clearTimeout(loginTimeoutId)
    }
  })
}

// ── Password reset ────────────────────────────────────────
export async function sendPasswordReset(email: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/accept-invite`,
  })
  if (error) throw error
}

// ── Invite user via Edge Function ─────────────────────────
// Falls back to direct creation when USE_SUPABASE_DB=false (offline/dev mode).
export async function inviteUser(payload: {
  email:     string
  role:      string
  companyId: string
  name:      string
}): Promise<{ userId?: string }> {
  if (!FLAGS.USE_SUPABASE_DB) {
    // Dev/offline fallback — create user record directly (no auth account created)
    const { fbSave: _fbSave } = await import('./db')
    const newId = crypto.randomUUID()
    await _fbSave('users', newId, {
      id: newId, name: payload.name, email: payload.email,
      role: payload.role, companyId: payload.companyId,
      invite_pending: true, createdAt: new Date().toISOString(),
    }, payload.companyId)
    return { userId: newId }
  }

  const { data: { session } } = await supabase.auth.getSession()
  if (!session) throw new Error('Not authenticated')

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-user`,
    {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        ...payload,
        redirectTo: `${window.location.origin}/accept-invite`,
      }),
    }
  )

  const json = await res.json() as { success?: boolean; userId?: string; error?: string }
  if (!res.ok || json.error) throw new Error(json.error ?? 'Invite failed')
  return { userId: json.userId }
}

// ── Set password (accept invite / password recovery) ──────
export async function setNewPassword(password: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ password })
  if (error) throw error
  // Clear invite_pending flag on the public.users row
  const { data: { user } } = await supabase.auth.getUser()
  if (user) {
    await supabase.from('users').update({
      invite_pending: false,
      updated_at: new Date().toISOString(),
    }).eq('id', user.id)
  }
}

// ── Delete account ────────────────────────────────────────
// Calls the delete_user SECURITY DEFINER RPC which wipes all
// company data and removes the auth.users row server-side.
// Then clears all local state and reloads.
export async function deleteAccount(): Promise<void> {
  // 1. Server-side: delete all docs / users / company + auth row
  const { error } = await supabase.rpc('delete_user')
  if (error) throw new Error(error.message)

  // 2. Local cleanup (mirrors doLogout)
  const { setUser }  = useAuthStore.getState()
  const { clearAll } = useDataStore.getState()

  queryClient.clear()
  idbClearAll().catch(() => {})
  resetSyncEngine()
  setUser(null)
  clearAll()
  clearEncryptionKey()

  // 3. Clear client-side Supabase session + reload
  sentryClearUser()
  try { await supabase.auth.signOut() } catch { /* auth row is gone — ignore */ }
  window.location.reload()
}

// ── Online/offline handler (legacy — kept for backward compat) ────
export function setupOnlineListener(): () => void {
  const { setOnline } = useAuthStore.getState()
  const handleOnline  = () => setOnline(true)
  const handleOffline = () => setOnline(false)
  window.addEventListener('online',  handleOnline)
  window.addEventListener('offline', handleOffline)
  return () => {
    window.removeEventListener('online',  handleOnline)
    window.removeEventListener('offline', handleOffline)
  }
}
